import { useMutation, type UseMutationResult } from '@tanstack/react-query'
import type { LoginLinkResponse } from '@acs/shared'
import { apiPost } from '@/lib/api'

/**
 * Builds a link that signs a human in as one customer, from the session this
 * system is already holding.
 *
 * ── A mutation, and not a query, on purpose ─────────────────────────
 * Nothing is changed by calling it, so `useQuery` is the obvious fit and is the
 * wrong one. The response carries a live bearer credential, and a query would
 * put it in the shared React Query cache under a key: retained across drawer
 * opens, visible in the devtools cache inspector, refetched on window focus
 * without anyone asking, and outliving the component that wanted it. A mutation
 * result lives on the one component instance and dies with it — which, since the
 * menu is keyed by customer, means the URL is discarded the moment the operator
 * looks at somebody else.
 *
 * `retry: false`: a failure here is a refusal (unknown customer, no email on the
 * row, rate limit) or a server that could not be reached. Neither is improved by
 * an automatic second attempt, and each attempt emits another credential.
 */
export function useLoginLink(): UseMutationResult<LoginLinkResponse, unknown, string> {
  return useMutation<LoginLinkResponse, unknown, string>({
    mutationFn: (id: string) =>
      apiPost<LoginLinkResponse>(`/api/customers/${encodeURIComponent(id)}/login-link`, {}),
    retry: false,
  })
}
